"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, jsx } from 'react/jsx-runtime';
import { isFormula } from '@membranehq/sdk';
import { BoundedPortal } from '../../FloatingPortalBoundary/index.mjs';
import { SvgIconType } from '../../SvgIcon/svg-icon-type.mjs';
import { withSlotScopeClasses } from '../../../helpers/scoped-slots.mjs';
import { toHeaderCase } from 'js-convert-case';
import { tv } from 'tailwind-variants';
import { Icon } from '../../../ui-kit/icon.mjs';
import { Root, Trigger, Positioner, Content, ItemGroup, ItemGroupLabel, Item } from '../../../ui-kit/styled/menu.mjs';
import { SvgIcon } from '../../SvgIcon/index.mjs';
import { getFieldSchemaOptions } from '../data-field.mjs';
import { useDataBuilderFieldCanUseVarFromVariables } from '../hooks.mjs';
import useDataBuilderField from '../use-data-builder-field.mjs';

const FIELD_TYPE_SELECTOR_MENU_DATA_ATTR = "data-field-type-selector-menu";
const DATA_ATTRIBUTES_PROPS = { [FIELD_TYPE_SELECTOR_MENU_DATA_ATTR]: true };
const recipe = tv({
  slots: withSlotScopeClasses("type_selector", {
    trigger: [
      "appearance-none border-0 bg-transparent [font-family:inherit] [font-size:inherit] [line-height:inherit]",
      "rounded px-1 flex items-center text-fg-muted font-medium gap-0.5 hover:bg-bg-subtle focus-visible:bg-bg-subtle"
    ],
    content: "z-popover bg-bg-subtle border border-border-muted px-2 py-1.5 rounded-lg min-w-48 w-auto flex flex-row items-start justify-start gap-5 shadow-2xl",
    item: [
      "flex flex-row gap-2 px-2 py-1 rounded m-0 font-normal w-full h-auto",
      "data-[selected=true]:font-bold data-[selected=true]:bg-bg-muted",
      "data-[highlighted]:bg-bg-emphasized",
      "hover:bg-bg-emphasized"
    ],
    groupContainer: "flex flex-col gap-0 w-full min-w-48",
    groupTitle: "text-xs text-fg-muted font-bold px-2 py-1 m-0"
  })
});
function FieldTypeSelector({
  onChange,
  onSchemaChange,
  ...rest
}) {
  const classes = recipe();
  const { field, localSchema } = useDataBuilderField();
  const showUseVarFromVariables = useDataBuilderFieldCanUseVarFromVariables();
  const schemaOptions = getFieldSchemaOptions(field).map((schemaOption) => ({
    value: schemaOption.type,
    label: toHeaderCase(schemaOption.type),
    iconType: SvgIconType[`SchemaType_${schemaOption.type}`],
    active: localSchema?.type === schemaOption.type
  }));
  const showTypeSelector = schemaOptions.length > 1;
  if (!showTypeSelector && !showUseVarFromVariables) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Root, { lazyMount: true, unmountOnExit: true, ...rest, children: [
    ":",
    /* @__PURE__ */ jsxs(Trigger, { className: classes.trigger(), "aria-label": "Change schema type", children: [
      localSchema?.type ?? "select",
      /* @__PURE__ */ jsx(Icon, { className: "w-3 h-4 pt-px", children: /* @__PURE__ */ jsx(SvgIcon, { type: SvgIconType.ChevronDown }) })
    ] }),
    /* @__PURE__ */ jsx(BoundedPortal, { children: /* @__PURE__ */ jsx(Positioner, { children: /* @__PURE__ */ jsxs(Content, { className: classes.content(), ...DATA_ATTRIBUTES_PROPS, children: [
      showTypeSelector && /* @__PURE__ */ jsx(TypeSelector, { options: schemaOptions, onChange: onSchemaChange }),
      showUseVarFromVariables && /* @__PURE__ */ jsx(FieldDropdown, { onChange })
    ] }) }) })
  ] });
}
function TypeSelector({
  options,
  onChange
}) {
  if (options.length <= 1) {
    return null;
  }
  return /* @__PURE__ */ jsx(GroupContainer, { title: "Field Type", children: options.map(({ value, iconType, label, active }) => /* @__PURE__ */ jsx(MenuItem, { onClick: () => onChange(value), icon: iconType, "data-selected": active, children: label }, String(value))) });
}
function FieldDropdown({ onChange }) {
  const { localSchema } = useDataBuilderField();
  switch (localSchema?.type) {
    case "object":
      return /* @__PURE__ */ jsx(FieldObjectDropdown, { onChange });
    case "array":
      return /* @__PURE__ */ jsx(FieldArrayDropdown, { onChange });
    default:
      return null;
  }
}
function FieldObjectDropdown({ onChange }) {
  const { field, setAutoOpen } = useDataBuilderField();
  function handleUseObjectFromVariables() {
    setAutoOpen(true);
  }
  function handleSetIndividualFields() {
    onChange(void 0);
  }
  const valueIsIndividualFields = !isFormula(field.value);
  return /* @__PURE__ */ jsx(GroupContainer, { title: "Field Value", children: valueIsIndividualFields ? /* @__PURE__ */ jsx(MenuItem, { onClick: handleUseObjectFromVariables, children: "Use object from variables" }) : /* @__PURE__ */ jsx(MenuItem, { onClick: handleSetIndividualFields, children: "Set individual fields" }) });
}
function FieldArrayDropdown({ onChange }) {
  const { field, setAutoOpen } = useDataBuilderField();
  function handleUseListFromVariables() {
    setAutoOpen(true);
  }
  function handleSetIndividualItems() {
    onChange(void 0);
  }
  const valueIsIndividualFields = !isFormula(field.value);
  return /* @__PURE__ */ jsx(GroupContainer, { title: "Field Value", children: valueIsIndividualFields ? /* @__PURE__ */ jsx(MenuItem, { onClick: handleUseListFromVariables, children: "Use list from variables" }) : /* @__PURE__ */ jsx(MenuItem, { onClick: handleSetIndividualItems, children: "Set individual items" }) });
}
function MenuItem({
  icon,
  children,
  ...props
}) {
  const classes = recipe();
  return /* @__PURE__ */ jsxs(Item, { ...props, className: classes.item(), value: String(children), children: [
    icon && /* @__PURE__ */ jsx(Icon, { children: /* @__PURE__ */ jsx(SvgIcon, { type: icon }) }),
    children
  ] });
}
function GroupContainer({ children, title }) {
  const classes = recipe();
  return /* @__PURE__ */ jsxs(ItemGroup, { className: classes.groupContainer(), children: [
    /* @__PURE__ */ jsx(ItemGroupLabel, { className: classes.groupTitle(), children: title }),
    children
  ] });
}

export { FIELD_TYPE_SELECTOR_MENU_DATA_ATTR, FieldTypeSelector };
